import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Steps,
    Switch,
    Table,
    Tag,
    Tooltip
} from "antd";
import {
    CheckSquareOutlined,
    FileDoneOutlined,
    IdcardOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined
} from "@ant-design/icons";
import Search from "antd/es/input/Search";
import style from "../objects/styles/profile.module.css";
import s2025 from "./styles/staff_2025.module.css";
import AddStaffForm from "./AddStaff";
import {instance} from "../../API/API";
import moment from "moment";

const {Option} = Select;
const {TextArea} = Input;

const FIO = ({name, lastname, patronymic}) => {
    return (
        <div className={s2025.fio__cont}>
            <div className={s2025.fio_ln}>{lastname}</div>
            <div className={s2025.fio_nm}>{`${name} ${patronymic}`}</div>
        </div>
    );
};

const Driver = ({number, fdate}) => {
    if (!number) return null;
    const final_date = fdate ? moment(+fdate * 1000).format('DD.MM.YY') : 'Нет данных'
    return (
        <div>
            <div className={s2025.driver_num__cont}>
                <IdcardOutlined style={{fontSize: 20}}/>
                <div className={s2025.driver_num}>{number}</div>
            </div>
            <div className={s2025.driver_fdate}>Годен до: <span style={{padding: '0 5px'}}>{final_date}</span></div>
        </div>
    )
}

const Roles = ({roles_ids, rules}) => {
    function convertStringToArray(idString) {
        if (!idString || typeof idString !== 'string') return [];
        return idString
            .split(',')
            .map(id => id.trim())
            .filter(id => id !== '');
    }

    const getRoleName = useMemo(() => {
        const roleMap = new Map();
        rules.forEach(role => {
            roleMap.set(role.id, role.long_name);
        });
        return (roleId) => roleMap.get(roleId.toString()) || 'Роль не найдена';
    }, [rules]);

    const idsArray = convertStringToArray(roles_ids);
    if (idsArray.length === 0) {
        return null;
    }
    return (
        <div>
            {idsArray.map(id => (
                <div key={id} className={s2025.role_text}>{getRoleName(id)}</div>
            ))}
        </div>
    );
};

// Компонент модального окна
const StaffModal = ({showModal, onCancel, onSave, initialData, rules}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();
    const [modifiedData, setModifiedData] = useState(null);
    const [selectedRoles, setSelectedRoles] = useState([]);

    useEffect(() => {
        if (initialData) {
            const roleArray = initialData.role ? initialData.role.split(',').filter(id => id.trim() !== '') : [];
            setSelectedRoles(roleArray);

            const formData = {
                // Основные поля staff
                firstname: initialData.firstname || '',
                lastname: initialData.lastname || '',
                patronymic: initialData.patronymic || '',
                login: initialData.login || '',
                store_id: initialData.store_id || null,
                enable_store: initialData.enable_store || null,
                status: initialData.status || 0,
                // Поля staff_data из info
                phone: initialData.info?.phone || '',
                email: initialData.info?.email || '',
                adress: initialData.info?.adress || '',
                nationality: initialData.info?.nationality || '',
                contract: initialData.info?.contract || 0,
                contract_fdate: initialData.info?.contract_fdate || '',
                first_parrent_name: initialData.info?.first_parrent_name || '',
                second_parrent_name: initialData.info?.second_parrent_name || '',
                first_parrent_phone: initialData.info?.first_parrent_phone || '',
                second_parrent_phone: initialData.info?.second_parrent_phone || '',
                birthday_date: initialData.info?.birthday_date ? moment.unix(initialData.info.birthday_date) : null,
                work_start_date: initialData.info?.work_start_date ? moment.unix(initialData.info.work_start_date) : null,
                passport: initialData.info?.passport || '',
                passport_fdate: initialData.info?.passport_fdate ? moment.unix(initialData.info.passport_fdate) : null,
                boravok: initialData.info?.boravok || '',
                boravok_fdate: initialData.info?.boravok_fdate ? moment.unix(initialData.info.boravok_fdate) : null,
                driver_license: initialData.info?.driver_license || '',
                driver_license_fdate: initialData.info?.driver_license_fdate ? moment.unix(initialData.info.driver_license_fdate) : null,
                kpi: initialData.info?.kpi || 0,
                language: initialData.info?.language || '',
                exp: initialData.info?.exp || '',
                personal_car: initialData.info?.personal_car || 0
            };

            form.setFieldsValue(formData);
            setModifiedData(null);
        }
    }, [initialData, form]);

    useEffect(() => {
        if (!showModal) {
            setIsEditing(false);
            setSelectedRoles([]);
            setModifiedData(null);
            form.resetFields();
        }
    }, [showModal, form]);

    const items = [
        {
            title: "Уволен",
            description: "Сотрудник не работает"
        },
        {
            title: "Дневница",
            description: "Получает дневницу"
        },
        {
            title: "Вне штата",
            description: "Сотрудник не в штате"
        },
        {
            title: "Сотрудник",
            description: "Получает полный оклад"
        },
    ]

    const handleRoleChange = (newRoles) => {
        setSelectedRoles(newRoles);
        setModifiedData(true); // Важно: отмечаем что есть изменения
    };

    const handleFormChange = (changedValues, allValues) => {
        setModifiedData(allValues);
    };

    const handleSave = async () => {
        try {
            if (!initialData || !initialData.id) {
                throw new Error('ID сотрудника не найден');
            }

            const values = await form.validateFields();
            console.log(values);
            // Преобразуем moment объекты в timestamp
            const processedData = {
                // Основные поля staff
                id: initialData.id,
                firstname: values.firstname || '',
                lastname: values.lastname || '',
                patronymic: values.patronymic || '',
                role: selectedRoles.join(','), // Используем selectedRoles
                login: values.login || '',
                store_id: values.store_id === '' ? null : values.store_id,
                enable_store: values.enable_store === '' ? null : values.enable_store,
                status: values.status === '' ? null : +values.status,

                // Поля staff_data
                phone: values.phone || '',
                email: values.email || '',
                adress: values.adress || '',
                nationality: values.nationality || '',
                contract: values.contract || 0,
                contract_fdate: values.contract_fdate || '',
                first_parrent_name: values.first_parrent_name || '',
                second_parrent_name: values.second_parrent_name || '',
                first_parrent_phone: values.first_parrent_phone || '',
                second_parrent_phone: values.second_parrent_phone || '',
                birthday_date: values.birthday_date ? values.birthday_date.unix() : '',
                work_start_date: values.work_start_date ? values.work_start_date.unix() : '',
                passport: values.passport || '',
                passport_fdate: values.passport_fdate ? values.passport_fdate.unix() : '',
                boravok: values.boravok || '',
                boravok_fdate: values.boravok_fdate ? values.boravok_fdate.unix() : '',
                driver_license: values.driver_license || '',
                driver_license_fdate: values.driver_license_fdate ? values.driver_license_fdate.unix() : '',
                kpi: values.kpi || 0,
                language: values.language || '',
                exp: values.exp || '',
                personal_car: values.personal_car || 0
            };

            // Убедимся, что все числовые поля преобразованы правильно
            const dataToSave = {
                ...processedData,
                contract: Number(processedData.contract) || 0,
                kpi: Number(processedData.kpi) || 0,
                personal_car: Number(processedData.personal_car) || 0,
                store_id: processedData.store_id === null ? null : Number(processedData.store_id) || null,
                enable_store: processedData.enable_store === null ? null : Number(processedData.enable_store) || null,
            };

            console.log('Данные для сохранения:', dataToSave);

            await onSave(dataToSave);
            setIsEditing(false);
            setModifiedData(null);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };


    const handleEdit = () => {
        setIsEditing(true);
        setModifiedData(true); // При входе в режим редактирования активируем кнопку
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setModifiedData(null);
        // Восстанавливаем исходные значения


        if (initialData) {
            const roleArray = initialData.role ? initialData.role.split(',').filter(id => id.trim() !== '') : [];
            setSelectedRoles(roleArray);

            const formData = {
                firstname: initialData.firstname || '',
                lastname: initialData.lastname || '',
                patronymic: initialData.patronymic || '',
                login: initialData.login || '',
                store_id: initialData.store_id || null,
                enable_store: initialData.enable_store || null,
                status: initialData.status || null,
                phone: initialData.info?.phone || '',
                email: initialData.info?.email || '',
                adress: initialData.info?.adress || '',
                nationality: initialData.info?.nationality || '',
                contract: initialData.info?.contract || 0,
                contract_fdate: initialData.info?.contract_fdate || '',
                first_parrent_name: initialData.info?.first_parrent_name || '',
                second_parrent_name: initialData.info?.second_parrent_name || '',
                first_parrent_phone: initialData.info?.first_parrent_phone || '',
                second_parrent_phone: initialData.info?.second_parrent_phone || '',
                birthday_date: initialData.info?.birthday_date ? moment.unix(initialData.info.birthday_date) : null,
                work_start_date: initialData.info?.work_start_date ? moment.unix(initialData.info.work_start_date) : null,
                passport: initialData.info?.passport || '',
                passport_fdate: initialData.info?.passport_fdate ? moment.unix(initialData.info.passport_fdate) : null,
                boravok: initialData.info?.boravok || '',
                boravok_fdate: initialData.info?.boravok_fdate ? moment.unix(initialData.info.boravok_fdate) : null,
                driver_license: initialData.info?.driver_license || '',
                driver_license_fdate: initialData.info?.driver_license_fdate ? moment.unix(initialData.info.driver_license_fdate) : null,
                kpi: initialData.info?.kpi || 0,
                language: initialData.info?.language || '',
                exp: initialData.info?.exp || '',
                personal_car: initialData.info?.personal_car || 0
            };
            form.setFieldsValue(formData);
        }
    };

    if (!showModal || !initialData) return null;

    return (
        <Modal
            title={`Сотрудник: ${initialData.lastname} ${initialData.firstname} ${initialData.patronymic}`}
            open={!!showModal}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Отмена
                </Button>,
                !isEditing ? (
                    <Button key="edit" type="primary" onClick={handleEdit}>
                        Редактировать
                    </Button>
                ) : (
                    <>
                        <Button key="cancelEdit" onClick={handleCancelEdit}>
                            Отменить редактирование
                        </Button>
                        <Button
                            key="save"
                            type="primary"
                            onClick={handleSave}
                            disabled={!modifiedData}
                        >
                            Сохранить
                        </Button>
                    </>
                )
            ]}
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                disabled={!isEditing}
            >
                {/* Основная информация */}
                <div style={{marginBottom: 10}}>
                    <h3>Основная информация</h3>
                    <Form.Item name="lastname" label="Фамилия">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="firstname" label="Имя">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="patronymic" label="Отчество">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="birthday_date" label="Дата рождения">
                        <DatePicker format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item name="login" label="Логин">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Роли сотрудника">
                        <Select
                            mode="multiple"
                            style={{width: '100%'}}
                            placeholder="Выберите роли"
                            value={selectedRoles}
                            onChange={handleRoleChange} // Используем наш обработчик
                            tagRender={(props) => {
                                const role = rules.find(r => r.id === props.value);
                                return (
                                    <Tag
                                        closable={props.closable}
                                        onClose={props.onClose}
                                        style={{margin: 2}}
                                        color={role ? 'blue' : 'default'}
                                    >
                                        {role ? role.long_name : props.value}
                                    </Tag>
                                );
                            }}
                        >
                            {rules.map(role => (
                                <Option key={role.id} value={role.id}>
                                    {role.long_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                {/* Контактная информация */}
                <div style={{marginBottom: 16}}>
                    <h3>Контактная информация</h3>
                    <Form.Item name="phone" label="Телефон">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="adress" label="Адрес">
                        <TextArea rows={2}/>
                    </Form.Item>
                    <Form.Item name="first_parrent_name" label="Имя первого родственника">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="first_parrent_phone" label="Телефон первого родственника">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="second_parrent_name" label="Имя второго родственника">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="second_parrent_phone" label="Телефон второго родственника">
                        <Input/>
                    </Form.Item>
                </div>

                {/* Личная информация */}
                <div style={{marginBottom: 16}}>
                    <h3>Документы</h3>
                    <Form.Item name="passport" label="Паспорт">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="passport_fdate" label="Срок действия паспорта">
                        <DatePicker format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item name="boravok" label="Боравок">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="boravok_fdate" label="Срок действия боравка">
                        <DatePicker format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item name="driver_license" label="Водительские права">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="driver_license_fdate" label="Срок действия прав">
                        <DatePicker format="DD.MM.YYYY"/>
                    </Form.Item>
                </div>

                {/* Рабочая информация */}
                <div style={{marginBottom: 16}}>
                    <h3>Рабочая информация</h3>
                    <Form.Item name="work_start_date" label="Дата начала работы">
                        <DatePicker format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item name="kpi" label="KPI">
                        <InputNumber min={0} max={100}/>
                    </Form.Item>
                    <Form.Item name="exp" label="Опыт работы">
                        <TextArea rows={2}/>
                    </Form.Item>
                    <Form.Item name="status" label="Статус">
                        <Steps
                            onChange={isEditing ? (value) => form.setFieldsValue({status: value}) : undefined}
                            //onChange={(value) => console.log(value)}
                            size="small"
                            items={items}
                            current={+form.getFieldValue('status')}
                        />


                    </Form.Item>
                </div>

                {/* Дополнительная информация */}
                <div style={{marginBottom: 16}}>
                    <h3>Дополнительная информация</h3>
                    <Form.Item name="nationality" label="Национальность">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="language" label="Язык">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="personal_car" label="Личный автомобиль" valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export const NewStaff = () => {

    const [selectedStaff, setSelectedStaff] = useState(null);
    const [load, setLoad] = useState(false);
    const [staff, setStaff] = useState([]);
    const [rules, setRules] = useState([]);
    const [searchName, setSearchName] = useState("");
    const [searchRole, setSearchRole] = useState("");
    const [sortStatus, setSortStatus] = useState(1);
    const [sortStatusRole, setSortStatusRole] = useState(1);
    const [change, setChange] = useState(0);
    const [addStaff, setAddStaff] = useState(false);


    const get_cookie = (cookie_name) => {
        const results = document.cookie.match('(^|;) ?' + cookie_name + '=([^;]*)(;|$)');
        return results ? unescape(results[2]) : null;
    };


    const allCookies = document.cookie;
    console.log(allCookies);


    const getStaffData = async () => {
        setLoad(true);
        try {
            const staff_req = await instance.get(`staff/get_staff.php?additional=true`);
            if (staff_req.data && staff_req.data.staff) {
                setStaff(staff_req.data.staff);
                const rules_req = await instance.get(`staff/get_rules.php?`);
                if (rules_req.data && rules_req.data.rules) {
                    setRules(rules_req.data.rules);
                }
            }
        } catch (e) {
            console.error("Error fetching staff data:", e);
        } finally {
            setLoad(false);
        }
    };


    const postNewStaff = async (staffData) => {
        console.log(staffData);
        setAddStaff(false)
        try {
            const response = await instance.post(`staff/add_staff.php`, staffData);
            if (response.data && response.data.success) {
                console.log("Staff member added successfully:", response.data);
                const get = await getStaffData()
                if (get) {
                    return response.data;
                }
                console.error("Failed to get staff", response.data);
                return {success: false, error: response.data.error || "Unknown error"};
            } else {
                console.error("Failed to add staff member:", response.data);
                return {success: false, error: response.data.error || "Unknown error"};
            }
        } catch (error) {
            console.error("Error adding staff member:", error);
            return {
                success: false,
                error: error.response?.data?.error || error.message
            };
        }
    };


    const handleSaveStaff = async (data) => {
        try {
            console.log('Отправляемые данные:', data);
            const response = await instance.post(`staff/update_staff.php`, data);
            const responseData = await response.data;
            console.log('Данные сохранены:', responseData);
            setChange(change + 1)
            setSelectedStaff(null);

        } catch (error) {
            console.error('Ошибка сохранения:', error);
        }
    };

    const sortedStaff = useMemo(() => {
        if (!staff || staff.length === 0) return [];
        let filteredData = staff;

        if (searchName.trim()) {
            const searchTerm = searchName.toLowerCase();
            filteredData = filteredData.filter(obj =>
                (obj.name && obj.name.toLowerCase().includes(searchTerm)) ||
                (obj.long_name && obj.long_name.toLowerCase().includes(searchTerm)) ||
                (obj.lastname && obj.lastname.toLowerCase().includes(searchTerm)) ||
                (obj.firstname && obj.firstname.toLowerCase().includes(searchTerm)) ||
                (obj.patronymic && obj.patronymic.toLowerCase().includes(searchTerm))
            );
        }

        if (sortStatusRole === 2) {
            filteredData = filteredData.filter(obj =>
                obj.role && obj.role.trim() !== '' && obj.role !== '0'
            );
        }

        if (searchRole.trim()) {
            const searchTerm = searchRole.toLowerCase();
            const hasMatchingRole = (staffObj) => {
                if (!staffObj.role || staffObj.role.trim() === '') return false;
                const roleIds = staffObj.role.split(',').map(id => id.trim()).filter(id => id !== '');
                return roleIds.some(roleId => {
                    const role = rules.find(r => r.id === roleId);
                    return role && (
                        role.name.toLowerCase().includes(searchTerm) ||
                        role.long_name.toLowerCase().includes(searchTerm)
                    );
                });
            };
            filteredData = filteredData.filter(hasMatchingRole);
        }

        switch (sortStatus) {
            case 1:
                return [...filteredData].sort((a, b) =>
                    (a.lastname || "").localeCompare(b.lastname || "")
                );
            case 2:
                return [...filteredData].sort((a, b) =>
                    (b.lastname || "").localeCompare(a.lastname || "")
                );
            default:
                return filteredData;
        }
    }, [staff, sortStatus, searchName, searchRole, sortStatusRole, rules]);

    useEffect(() => {
        getStaffData();
    }, [change]);

    const handleSortToggle = (newSortStatus) => {
        setSortStatus(prev => prev === newSortStatus ? 0 : newSortStatus);
    };

    const handleSortRoleToggle = () => {
        setSortStatusRole(prev => prev === 1 ? 2 : 1);
    };

    const columns = [
        {
            title: 'id',
            key: 'ids',
            dataIndex: 'id',
            align: 'center',
            width: 50,
        },
        {
            title: (
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <Search
                        className={style.search}
                        placeholder={'ФИО'}
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        allowClear
                    />
                    <div className={style.sortIcon__cont}>
                        <Tooltip title={'Сортировка А - Я'} placement="top">
                            <SortAscendingOutlined
                                style={{
                                    color: sortStatus === 1 ? 'green' : '#ccc',
                                    cursor: 'pointer',
                                    marginLeft: 8
                                }}
                                onClick={() => handleSortToggle(1)}
                            />
                        </Tooltip>
                        <Tooltip title={'Сортировка Я - А'} placement="top">
                            <SortDescendingOutlined
                                style={{
                                    color: sortStatus === 2 ? 'green' : '#ccc',
                                    cursor: 'pointer',
                                    marginLeft: 4
                                }}
                                onClick={() => handleSortToggle(2)}
                            />
                        </Tooltip>
                    </div>
                </div>
            ),
            dataIndex: 'firstname',
            key: 'firstname',
            align: 'left',
            render: (text, record) => (
                <FIO
                    name={text}
                    lastname={record.lastname}
                    patronymic={record.patronymic}
                />
            )
        },
        {
            title: (
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <Search
                        className={style.search}
                        placeholder={'Поиск по ролям'}
                        value={searchRole}
                        onChange={e => setSearchRole(e.target.value)}
                        allowClear
                        style={{flex: 1}}
                    />
                    <div className={style.sortIcon__cont}>
                        <Tooltip
                            title={sortStatusRole === 1 ? 'Показать только с ролями' : 'Показать всех'}
                            placement="top"
                        >
                            <CheckSquareOutlined
                                style={{
                                    color: sortStatusRole === 2 ? 'green' : '#ccc',
                                    cursor: 'pointer',
                                    marginLeft: 8
                                }}
                                onClick={() => handleSortRoleToggle()}
                            />
                        </Tooltip>
                    </div>
                </div>
            ),
            key: 'role',
            dataIndex: 'role',
            width: 280,
            render: (text, record) => (
                <Roles
                    roles_ids={text}
                    rules={rules}
                />
            )
        },
        {
            title: "Боравок",
            key: 'boravok',
            dataIndex: 'info',
            width: 250,
            render: (text, record) => (
                <Driver
                    number={record.info?.boravok}
                    fdate={record.info?.boravok_fdate}
                />
            )
        },
        {
            title: "Водительские права",
            key: 'driver',
            dataIndex: 'info',
            width: 200,
            render: (text, record) => (
                <Driver
                    number={record.info?.driver_license}
                    fdate={record.info?.driver_license_fdate}
                />
            )
        },
        {
            title: 'Анкета',
            key: 'id',
            align: 'center',
            width: 50,
            render: (_, record) => (
                <Space size="middle" onClick={() => setSelectedStaff(record)}>
                    <FileDoneOutlined style={{fontSize: '20px', color: 'green', cursor: 'pointer'}}/>
                </Space>
            ),
        },
    ];

    return (
        <>
            <div style={{margin: '10px'}}>
                <Button size={'small'} onClick={() => setAddStaff(true)} type={'primary'}>Добавить сотрудника</Button>
            </div>
            <Table
                columns={columns}
                pagination={{
                    position: ['none', 'bottomRight'],
                    pageSize: 10,
                    showSizeChanger: false
                }}
                dataSource={sortedStaff}
                loading={load}
                size={'small'}
                rowKey="id"
                locale={{emptyText: "Нет данных о сотрудниках"}}
                className="my-table"
                rowClassName={() => ''}
            />

            <StaffModal
                showModal={selectedStaff}
                onCancel={() => setSelectedStaff(null)}
                onSave={handleSaveStaff}
                initialData={selectedStaff}
                rules={rules}
            />
            <Modal width={'900px'} open={addStaff} onOk={() => setAddStaff(false)} onCancel={() => setAddStaff(false)}>
                <AddStaffForm onAddStaff={postNewStaff} onCancel={() => setAddStaff(false)}/>
            </Modal>

        </>
    );
};

